import {trackLink} from "@/functions/tealium";

const scopes = {}
const activeTools = []

const AGENT_HEADER = 'X-TH-WebMCP'
const SESSION_KEY = 'webmcp_session_id'
let markedFetch

export class ToolError extends Error {
	constructor(message, code = 'TOOL_FAILED', retryable = false) {
		super(message)
		this.code = code
		this.retryable = retryable
	}
}

const isFlagOn = (value) => {
	try {
		return JSON.parse(value) === true
	} catch {
		return false
	}
}

export const isWebMcpEnabled = () => isFlagOn(process.env.VUE_APP_WEBMCP_ENABLED)

export const isWebMcpActionsEnabled = () => isWebMcpEnabled() && isFlagOn(process.env.VUE_APP_WEBMCP_ACTIONS_ENABLED)

export const findModelContext = () => {
	if (typeof document !== 'undefined' && typeof document.modelContext?.registerTool === 'function') {
		return document.modelContext
	}
	if (typeof navigator !== 'undefined' && typeof navigator.modelContext?.registerTool === 'function') {
		return navigator.modelContext
	}
	return undefined
}

const describeError = (error) => (error instanceof Error ? error.message : String(error))

const isAbortError = (error) => error instanceof DOMException && error.name === 'AbortError'

const toFailure = (error) => ({
	error: describeError(error),
	errorCode: error instanceof ToolError ? error.code : 'TOOL_FAILED',
	retryable: error instanceof ToolError ? error.retryable : false,
})

const withErrorCode = (result) => (result?.error && !result.errorCode ? {...result, errorCode: 'TOOL_FAILED', retryable: false} : result)

const toJsonValue = (result) => (result === undefined ? null : JSON.parse(JSON.stringify(result)))

const isPlainObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value)

const findUnknownKey = (schema, value, path) => {
	if (!isPlainObject(value)) return undefined
	const properties = schema?.properties ?? {}
	const accepted = Object.keys(properties)
	const unknown = schema?.additionalProperties === false ? Object.keys(value).find((key) => !accepted.includes(key)) : undefined
	if (unknown) return {path: path ? `${path}.${unknown}` : unknown, accepted}
	return Object.entries(value).map(([key, nested]) => (properties[key] ? findUnknownKey(properties[key], nested, path ? `${path}.${key}` : key) : undefined)).find(Boolean)
}

const SECRET_KEY = /password|passcode|code|otp|totp|secret/i

const checkInput = (schema, input) => {
	if (input === undefined || input === null) return {}
	if (!isPlainObject(input)) throw new ToolError('The input must be a JSON object.', 'INVALID_INPUT')
	const unknown = findUnknownKey(schema, input, '')
	if (unknown && SECRET_KEY.test(unknown.path)) {
		throw new ToolError(`${unknown.path} is not accepted. Passwords and codes never pass through a tool: the person, their password manager or your secure sign-in enters them on the page, and the tool waits for the field to be filled.`, 'INVALID_INPUT')
	}
	if (unknown) {
		throw new ToolError(`Unknown input ${unknown.path}. ${unknown.accepted.length ? `Accepted here: ${unknown.accepted.join(', ')}.` : 'This tool takes no input.'}`, 'INVALID_INPUT')
	}
	return input
}

export const activeToolName = () => activeTools[activeTools.length - 1]

const isOwnApi = (resource) => {
	try {
		const url = new URL(resource instanceof Request ? resource.url : String(resource), location.href)
		return url.origin === location.origin && url.pathname.startsWith('/api')
	} catch {
		return false
	}
}

export const markAgentRequests = () => {
	if (typeof globalThis.fetch !== 'function' || globalThis.fetch === markedFetch) return
	const pageFetch = globalThis.fetch
	markedFetch = (resource, options) => {
		const toolName = activeToolName()
		if (!toolName || !isOwnApi(resource)) return pageFetch(resource, options)
		const headers = new Headers(options?.headers ?? (resource instanceof Request ? resource.headers : undefined))
		headers.set(AGENT_HEADER, toolName)
		return pageFetch(resource, {...options, headers})
	}
	globalThis.fetch = markedFetch
}

const readConsentCookie = () => {
	const match = document.cookie.match(/(^|;)\s*CONSENTMGR\s*=\s*([^;]+)/)
	if (!match) return {}
	try {
		return Object.fromEntries(decodeURIComponent(match[2]).split('|').map((pair) => [pair.slice(0, pair.indexOf(':')), pair.slice(pair.indexOf(':') + 1)]))
	} catch {
		return {}
	}
}

export const isPrivacyOptedOut = () => navigator.globalPrivacyControl === true || readConsentCookie().dns === 'true'

const readSessionId = () => {
	try {
		const existing = sessionStorage.getItem(SESSION_KEY)
		if (existing) return existing
		const created = crypto.randomUUID()
		sessionStorage.setItem(SESSION_KEY, created)
		return created
	} catch {
		return undefined
	}
}

const agentEventsUrl = () => {
	const value = process.env.VUE_APP_AGENT_EVENTS_URL ?? ''
	return /^https:\/\/[^\s]+$/.test(value) ? value : ''
}

const sendAgentEvent = (event) => {
	const url = agentEventsUrl()
	if (!url) return
	const body = JSON.stringify({
		...event,
		site: 'thauth',
		brand: sessionStorage.getItem('appName') || undefined,
		occurredAt: new Date().toISOString(),
		...(isPrivacyOptedOut() ? {} : {sessionId: readSessionId()}),
	})
	if (!navigator.sendBeacon?.(url, new Blob([body], {type: 'application/json'}))) {
		fetch(url, {method: 'POST', body, headers: {'content-type': 'application/json'}, keepalive: true}).catch(() => undefined)
	}
}

const summarizeResult = (result) => {
	const alerts = result?.alerts ?? result?.step?.alerts ?? []
	return {
		result: result?.errorCode ?? result?.outcome ?? 'returned',
		nextRoute: result?.outcome === 'next_step' ? result.step?.path : undefined,
		alertTypes: alerts.length ? [...new Set(alerts.map((alert) => alert.type))] : undefined,
		alertFields: alerts.some((alert) => alert.field) ? alerts.map((alert) => alert.field).filter(Boolean) : undefined,
		captchaPrompt: result?.waitingFor === 'captcha' || undefined,
		passkeyPrompt: result?.waitingFor === 'passkey' || undefined,
	}
}

const trackToolCall = (name, outcome, result, startedAt) => {
	const durationMs = Math.round(performance.now() - startedAt)
	const route = location.pathname
	const category = document.body?.dataset.category || undefined
	const summary = summarizeResult(result)
	trackLink({
		tealium_event: 'webmcp_tool',
		tool_name: name,
		tool_outcome: outcome,
		tool_result: summary.result,
		tool_duration_ms: durationMs,
		tool_category: category,
		page_name: route,
	})
	sendAgentEvent({event: 'webmcp_tool', tool: name, outcome, durationMs, route, category, ...summary})
}

const tagAgentFunnel = () => {
	try {
		if (sessionStorage.getItem('utm_content') === null) sessionStorage.setItem('utm_content', 'webmcp')
	} catch {
		return
	}
}

const withTracking = (tool) => {
	const {isAction: _isAction, ...registered} = tool
	return {
		...registered,
		execute: async (input, context) => {
			const startedAt = performance.now()
			activeTools.push(tool.name)
			tagAgentFunnel()
			try {
				const result = toJsonValue(withErrorCode(await tool.execute(checkInput(tool.inputSchema, input), context)))
				trackToolCall(tool.name, result?.error ? 'error' : 'success', result, startedAt)
				return result
			} catch (error) {
				if (!(error instanceof ToolError)) console.error('webMcp tool error:', tool.name, error)
				const failure = toFailure(error)
				trackToolCall(tool.name, 'error', failure, startedAt)
				return failure
			} finally {
				activeTools.splice(activeTools.lastIndexOf(tool.name), 1)
			}
		},
	}
}

export const registeredToolNames = () => Object.values(scopes).flatMap((scope) => scope.names)

const ourToolNames = () => new Set(registeredToolNames())

const warnAboutUnexpectedTools = async (modelContext) => {
	if (typeof modelContext.getTools !== 'function') return
	const ours = ourToolNames()
	const unexpected = (await modelContext.getTools()).map((tool) => tool.name).filter((name) => !ours.has(name))
	if (unexpected.length) {
		console.warn(`WebMCP tools registered on this origin by another script: ${unexpected.join(', ')}`)
	}
}

const toolSignature = (tools) => JSON.stringify(tools.map(({name, description, inputSchema, annotations}) => [name, description, inputSchema, annotations]))

const unregisterByName = (modelContext, names) => {
	if (typeof modelContext?.unregisterTool !== 'function') return Promise.resolve()
	const claimed = new Set(registeredToolNames())
	return Promise.allSettled(names.filter((name) => !claimed.has(name)).map((name) => {
		try {
			return Promise.resolve(modelContext.unregisterTool(name))
		} catch (error) {
			return Promise.reject(error)
		}
	}))
}

export const unregisterTools = (scopeName) => {
	const scope = scopes[scopeName]
	if (!scope) return Promise.resolve()
	scope.controller.abort()
	delete scopes[scopeName]
	return unregisterByName(findModelContext(), scope.names)
}

export const registerTools = async (scopeName, tools) => {
	const signature = toolSignature(tools)
	if (scopes[scopeName]?.signature === signature) return scopes[scopeName].names
	await unregisterTools(scopeName)
	unregisterTools(scopeName)
	const modelContext = findModelContext()
	if (!modelContext || !tools.length) return []

	const controller = new AbortController()
	const scope = {controller, signature, names: tools.map((tool) => tool.name)}
	scopes[scopeName] = scope

	const outcomes = await Promise.allSettled(tools.map((tool) => modelContext.registerTool(withTracking(tool), {signal: controller.signal})))
	const failures = outcomes.flatMap((outcome, index) => (
		outcome.status === 'rejected' && !isAbortError(outcome.reason) ? [`${tools[index].name}: ${describeError(outcome.reason)}`] : []
	))
	if (failures.length) {
		console.warn(`WebMCP tool registration failed. ${failures.join('; ')}`)
	}
	scope.names = tools.filter((_tool, index) => outcomes[index].status === 'fulfilled').map((tool) => tool.name)
	if (controller.signal.aborted) {
		await unregisterByName(modelContext, scope.names)
		return scope.names
	}

	modelContext.addEventListener?.('toolchange', () => warnAboutUnexpectedTools(modelContext), {signal: controller.signal})
	await warnAboutUnexpectedTools(modelContext)
	return scope.names
}
